<script setup lang="ts">
  import { ref, onMounted, onUnmounted } from 'vue';
  import { useModal } from 'vue-final-modal';
  import { search, type Position } from '@/entities/Position';
  import {
    CopyPositionModal,
    CreatePositionsModal,
    EditPositionModal,
    PositionsTable,
  } from '../../ui';
  import PositionFilterModal from './PositionFilterModal.vue';
  import AdminListPageLayout from '../AdminListPageLayout.vue';
  import {
    usePositionManagement,
    useAdminListPageSetup,
    createAdminListPageProps,
  } from '../../lib';
  import { DEFAULT_ORDER_OPTION } from '@/shared/config';
  import { useTableFilterModal } from '@/shared/lib/use';
  import { usePositionsExport } from './usePositionsExport';

  const props = defineProps(createAdminListPageProps());

  const { createPosition, updatePosition, loadPosition, clonePermissions } =
    usePositionManagement();
  const selectedPosition = ref<Position | null>(null);

  const {
    items: positions,
    loading,
    handleSearch,
    initSearch,
    metadata,
    currentPage,
    selectedQuantity,
    onPageChanged,
    handleQuantityChange,
    searchTerm,
    removeItems,
    cleanup,
    handleFilter,
    handleSortOptions,
    filter,
    isUsingFilters,
    handleTableFilter,
    sort,
    order,
  } = useAdminListPageSetup<Position>(
    {
      defaultSearchUrl: '/positions',
      searchFunction: search,
      defaultAmount: 10,
    },
    props
  );

  const orderOptions = [
    { ...DEFAULT_ORDER_OPTION, label: 'Por mais recente' },
    { label: 'Por mais antigo', sort: 'createdAt', order: 'asc' },
    { label: 'Por ordem alfabética crescente', sort: 'name', order: 'asc' },
    { label: 'Por ordem alfabética decrescente', sort: 'name', order: 'desc' },
  ];

  const localFilterStore = {
    clearFilters: () => {},
    filters: {},
  };

  const { isFilterActive, openFilterModal } = useTableFilterModal(
    PositionFilterModal,
    localFilterStore,
    handleFilter,
    () => filter.value
  );

  const handleFilterClicked = () => {
    openFilterModal();
  };

  const createPositionsModal = useModal({
    component: CreatePositionsModal,
    attrs: {
      title: 'Novo Cargo',
      onClose() {
        createPositionsModal.close();
      },
      async onSave(data: { position: string; departmentId: number }) {
        const { success } = await createPosition(data);
        if (success) {
          createPositionsModal.close();
          await initSearch();
        }
      },
    },
  });

  const editPositionModal = useModal({
    component: EditPositionModal,
    attrs: {
      get title() {
        return 'Editar Cargo';
      },
      get position() {
        return selectedPosition.value;
      },
      onClose() {
        editPositionModal.close();
        selectedPosition.value = null;
      },
      async onSave(data: { position: string; departmentId: number }) {
        if (selectedPosition.value) {
          const { success } = await updatePosition(selectedPosition.value.id, {
            name: data.position,
            departmentId: data.departmentId,
          });
          if (success) {
            editPositionModal.close();
            await initSearch();
          }
        }
      },
    },
  });

  const copyPositionModal = useModal({
    component: CopyPositionModal,
    attrs: {
      get title() {
        return 'Clonar Permissões';
      },
      get position() {
        return selectedPosition.value;
      },
      onClose() {
        copyPositionModal.close();
        selectedPosition.value = null;
      },
      async onSave(data: { sourcePositionId: number }) {
        if (selectedPosition.value) {
          const { success } = await clonePermissions(
            data.sourcePositionId,
            selectedPosition.value.id
          );
          if (success) {
            copyPositionModal.close();
            selectedPosition.value = null;
          }
        }
      },
    },
  });

  const handleCreatePositions = () => {
    createPositionsModal.open();
  };

  const handleEdit = async (id: number) => {
    const position = await loadPosition(id);
    if (position) {
      selectedPosition.value = position;
      editPositionModal.open();
    }
  };

  const handleCopy = async (id: number) => {
    const position = await loadPosition(id);
    if (position) {
      selectedPosition.value = position;
      copyPositionModal.open();
    }
  };

  const { handleExportToExcel } = usePositionsExport({
    searchTerm,
    sort,
    order,
    filter,
  });

  const handleDelete = async (ids: number[]) => {
    await removeItems(ids);
  };

  onMounted(async () => {
    await initSearch();
  });

  onUnmounted(() => {
    cleanup();
  });
</script>

<template>
  <AdminListPageLayout
    data-testid="admin-positions-page"
    title="Cargos"
    search-placeholder="Buscar cargos..."
    create-button-text="Novo Cargo"
    entity-name="cargo"
    empty-state-message="Nenhum cargo cadastrado"
    :items="positions"
    :loading="loading"
    :search-term="searchTerm"
    :metadata="metadata"
    :selected-quantity="selectedQuantity"
    :is-using-filters="isUsingFilters"
    :current-page="currentPage"
    @search="handleSearch"
    @create="handleCreatePositions"
    @export="handleExportToExcel"
    @page-changed="onPageChanged"
    @update:selected-quantity="handleQuantityChange"
  >
    <template #table>
      <PositionsTable
        :positions="positions"
        :loading="loading"
        :order-options="orderOptions"
        :is-filter-active="isFilterActive"
        :search-term="searchTerm"
        @delete="handleDelete"
        @edit="handleEdit"
        @copy="handleCopy"
        @update:sort="handleSortOptions"
        @update:filter="handleTableFilter"
        @filter-clicked="handleFilterClicked"
      />
    </template>
  </AdminListPageLayout>
</template>
